import { useEffect, useState } from 'react';
import { Box, Divider, IconButton, SvgIcon } from '@mui/material';
import { getUserType, type UserType } from 'entities/user';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from 'shared/api';
import { useUser, queryClient } from 'shared/api/tanstack';
import AgentsIcon from 'shared/assets/icons/agents.svg';
import GuideIcon from 'shared/assets/icons/guid.svg';
import LeasingIcon from 'shared/assets/icons/leasing.svg';
import LogoutIcon from 'shared/assets/icons/logout.svg';
import SelectIcon from 'shared/assets/icons/select.svg';
import { ROUTE_PATHS } from 'shared/model/constants/paths';
import { LogoLink } from 'shared/ui/logo-link';
import styles from './dashboard-left-bar.module.scss';

const LetfBarItem = ({
  title,
  count,
  icon,
  click,
}: {
  title: string;
  count: number;
  icon: SVGIcon;
  click: () => void;
}) => {
  return (
    <Box className={styles.agentsLink} onClick={click}>
      <SvgIcon
        component={icon}
        className={styles.svgLogout}
        inheritViewBox
        sx={{ fontSize: '18px' }}
      />
      <div className={styles.linkWrapper}>
        <span className={styles.spanText}>{title}</span>
        {!!count && <div className={styles.counter}>{count}</div>}
      </div>
    </Box>
  );
};

export const DashboardLeftBar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { data: user } = useUser();

  const [isAdmin, setIsAdmin] = useState(location.pathname.includes('/admin'));
  const [userType, setUserType] = useState<UserType | undefined>();

  useEffect(() => {
    setIsAdmin(user?.isAdmin);
    setUserType(user?.user_type);
  }, [user]);

  const [leasingCount, setLeasingCount] = useState<any>(0);
  const [selectCount, setSelectCount] = useState<any>(0);
  const [counter, setCounter] = useState<any>({
    agents: {
      new: 0,
      all: 1,
    },
    leasings: {
      all: 0,
      new: 0,
    },
    suppliers: {
      all: 0,
      new: 0,
    },
    clients: {
      all: 0,
      new: 0,
    },
  });

  const getGuide = (event: any) => {
    event?.preventDefault();
    const link = document.createElement('a');
    if (user?.agent) {
      link.href = '/Руководство_партнера.pdf';
      link.download = 'Руководство_партнера.pdf';
    } else {
      link.href = '/Руководство_пользователя.pdf';
      link.download = 'Руководство_пользователя.pdf';
    }
    link.click();
  };

  const getCountApp = async () => {
    try {
      const response = await apiClient.get('/select-applications/count');
      const responseLeas = await apiClient.get('/leasing-applications/count');
      const responseAll = await apiClient.get('/select-applications/allCount');

      setSelectCount(response?.data?.data?.count || 0);
      setLeasingCount(responseLeas?.data?.data?.count || 0);

      if (responseAll?.data?.data?.users) {
        setCounter(responseAll.data?.data?.users);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      getCountApp();
    }
  }, [window.location.href]);

  const logout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    queryClient.clear();
    navigate('/auth');
  };

  return (
    <Box className={styles.leftBar}>
      <LogoLink dashboard admin={isAdmin} />

      <Box className={styles.adminLinks}>
        {
          <>
            <LetfBarItem
              title={'Заявки на подбор'}
              count={selectCount}
              icon={SelectIcon}
              click={() =>
                navigate(isAdmin ? '/dashboard/admin/all/select' : '/dashboard/all/select')
              }
            />
            <LetfBarItem
              title={'Заявки на лизинг'}
              count={leasingCount}
              icon={LeasingIcon}
              click={() =>
                navigate(isAdmin ? '/dashboard/admin/all/leasing' : '/dashboard/all/leasing')
              }
            />
            {isAdmin && (
              <>
                <Divider />
                <LetfBarItem
                  title={'Поставщики'}
                  count={counter.suppliers.new}
                  icon={AgentsIcon}
                  click={() => navigate('/dashboard/admin/users/suppliers')}
                />
                <LetfBarItem
                  title={'Лизинг'}
                  count={counter.leasings.new}
                  icon={AgentsIcon}
                  click={() => navigate('/dashboard/admin/users/leasings')}
                />
                <LetfBarItem
                  title={'Агенты'}
                  count={counter.agents.new}
                  icon={AgentsIcon}
                  click={() => navigate('/dashboard/admin/users/agents')}
                />
                <LetfBarItem
                  title={'Клиенты'}
                  count={counter.clients.new}
                  icon={AgentsIcon}
                  click={() => navigate('/dashboard/admin/users/clients')}
                />
                <Divider />
                <LetfBarItem
                  title={'Каталог'}
                  count={0}
                  icon={SelectIcon}
                  click={() => navigate('/dashboard/admin/catalog')}
                />
                <LetfBarItem
                  title={'Заявки с лендингов'}
                  count={0}
                  icon={SelectIcon}
                  click={() => navigate(ROUTE_PATHS.ADMIN_LEADS)}
                />
              </>
            )}
            {(isAdmin || ['agent', 'client'].includes(userType!)) && (
                <LetfBarItem
                  title={'Черновики'}
                  count={0}
                  icon={SelectIcon}
                  click={() => navigate(ROUTE_PATHS.DRAFTS)}
                />
              )}
            <LetfBarItem
              title={'Рефералы'}
              count={0}
              icon={SelectIcon}
              click={() => navigate(ROUTE_PATHS.REFERALS)}
            />
          </>
        }
      </Box>

      {user && (
        <div className={styles.userBlockWrapper}>
          {!isAdmin && (
            <Box className={styles.agentsLink} onClick={() => getGuide(event)}>
              <SvgIcon
                component={GuideIcon}
                className={styles.svgLogout}
                inheritViewBox
                sx={{ fontSize: '18px' }}
              />
              <span className={styles.spanText}>Инструкция</span>
            </Box>
          )}

          <Box className={styles.userBlock}>
            <Box
              className={styles.userInfo}
              onClick={() => navigate(ROUTE_PATHS.USER_PROFILE)}
              sx={{ cursor: 'pointer', flex: 1 }}
            >
              <span className={styles.userName}>
                {user.bio} ({getUserType(user)})
              </span>
              <span className={styles.userMail}>{user.email}</span>
            </Box>
            <IconButton
              onClick={logout}
              sx={{ width: '20px', height: '20px', marginRight: '-5px' }}
              className={styles.logoutBtn}
              edge='end'
              disableRipple
            >
              <SvgIcon
                component={LogoutIcon}
                className={styles.svgLogout}
                inheritViewBox
                sx={{ fontSize: '19px' }}
              />
            </IconButton>
          </Box>
        </div>
      )}
    </Box>
  );
};
